import { useState, useEffect, useCallback, useRef } from 'react';
import { getArticles, getFeaturedArticles, getTrendingArticles, getArticle, getCategories, getMenu, searchArticles, getWidgets } from '../utils/api';

// ===== useFetch =====
export const useFetch = (fetchFn, deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const execute = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn();
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line
  }, deps);

  useEffect(() => { execute(); }, [execute]);

  return { data, loading, error, refetch: execute };
};

// ===== useArticles =====
export const useArticles = (params = {}) => {
  const paramsStr = JSON.stringify(params);
  return useFetch(() => getArticles(params), [paramsStr]);
};

// ===== useFeatured =====
export const useFeatured = () => useFetch(getFeaturedArticles, []);

// ===== useTrending =====
export const useTrending = () => useFetch(getTrendingArticles, []);

// ===== useArticle =====
export const useArticle = (slug) => useFetch(() => getArticle(slug), [slug]);

// ===== useCategories =====
export const useCategories = () => useFetch(getCategories, []);

// ===== useMenu =====
export const useMenu = () => useFetch(getMenu, []);

// ===== useWidgets =====
export const useWidgets = (articleId) =>
  useFetch(() => getWidgets(articleId), [articleId]);

// ===== useSearch =====
export const useSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  const search = useCallback((q) => {
    setQuery(q);
    clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await searchArticles(q);
        setResults(res.data || []);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, []);

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setError(null);
  }, []);

  return { query, results, loading, error, search, clearSearch };
};

// ===== useIntersectionObserver =====
export const useIntersectionObserver = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
      if (entry.isIntersecting) setHasBeenVisible(true);
    }, { threshold: 0.1, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return { ref, isVisible, hasBeenVisible };
};

// ===== useScrollProgress =====
export const useScrollProgress = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = (window.scrollY / totalHeight) * 100;
      setProgress(Math.min(100, Math.max(0, scrolled)));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return progress;
};

// ===== useLocalStorage =====
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch { return initialValue; }
  });

  const setValue = useCallback((value) => {
    try {
      setStoredValue(value);
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (err) { console.error(err); }
  }, [key]);

  return [storedValue, setValue];
};
